/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ISwatchItem, ProductComponentSwatchComponent, RatingComponent } from '@msdyn365-commerce/components';
import {
    getCatalogId,
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    msdyn365Commerce,
    getUrlSync
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    AttributeValue,
    CommerceProperty,
    ProductDimension,
    ProductPrice,
    ProductSearchResult,
    ProductType
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp,
    StringExtensions,
    validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick,
    IncrementalQuantity
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
import AddToCartComponent from './addtocart.component';
import { ISearchResultContainerResources } from '../../definition-extensions/search-result-container.ext.props.autogenerated';

export interface IProductComponentProps extends IComponentProps<{ product?: ProductSearchResult; productPrice?: ProductPrice }> {
    className?: string;
    productPrices?: ProductPrice[];
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isEnabledProductDescription?: boolean;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    loginRegisterResource?: string;
    resources?: ISearchResultContainerResources;
}

export interface IProductComponent extends IComponent<IProductComponentProps> {}

const PriceComponentActions = {};

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;
    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */

/**
 * Gets the react node for product availability.
 * @param inventoryAvailabilityLabel - The product information.
 * @returns The node representing markup for product availability.
 */
function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
    if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
        return null;
    }

    return (
        <div className='msc-product__availability'>
            <span>{inventoryAvailabilityLabel}</span>
        </div>
    );
}

/**
 * Renders product comparison button similar to the quick view button.
 * @param productComparisonButton - React element of the button.
 * @param product - Current product info.
 * @param catalogId - Current catalog.
 * @returns React element for the specific product.
 */
function renderProductComparisonButton(
    productComparisonButton: React.ReactNode,
    product: ProductSearchResult,
    catalogId: number
): JSX.Element | undefined {
    validateCatalogId(catalogId);
    return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
}

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

const ProductCard: React.FC<IProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    ratingAriaLabel,
    ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    productPrices,
    telemetryContent,
    quickViewButton,
    productComparisonButton,
    isEnabledProductDescription,
    isPriceMinMaxEnabled,
    priceResources,
    inventoryLabel,
    dimensionAvailabilities,
    swatchItemAriaLabel,
    loginRegisterResource,
    resources
}) => {
    const product = data.product;
    // Customization to see if user is authenticated and use it where we need to hide price
    const isAuthenticated = context?.request?.user?.isAuthenticated;
    const user = context?.request?.user;
    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    const enableStockCheck = context.app.config.enableStockCheck;

    const siteContext = context as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    /**
     * Updates the product page and Image url based on swatch selected.
     * @param coreContext - Context of the caller.
     * @param swatchItem - Dimension swatch selected.
     */
    const updatePageAndImageUrl = React.useCallback(
        (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
            const dimensionType = swatchItem.dimensionType;
            selectedSwatchItems.setValue(dimensionType, swatchItem);
            if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
                return;
            }
            const queryString = `${dimensionType}=${swatchItem.value}`;
            let productPageUrlWithSwatch = '';
            if (productPageUrl.includes(dimensionType)) {
                const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
                newUrl.searchParams.delete(dimensionType);
                productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
            } else {
                productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
            }
            setProductPageUrl(productPageUrlWithSwatch);
            if (dimensionType === dimensionToPreSelectInProductCard) {
                const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
                    ? swatchItem.productImageUrls[0]
                    : undefined;
                const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
                setProductImageUrl(newImageUrl);
            }
        },
        [selectedSwatchItems, context, productPageUrl]
    );

    if (!product) {
        return null;
    }

    /**
     * Checks if rendering the particular dimensions is allowed for product card.
     * @param dimensionType - Dimension to be displayed.
     * @returns Updates the state with new product page url.
     */
    function shouldDisplayDimension(dimensionType: string): boolean {
        const dimensionsContext = context as ICoreContext<IDimensionsApp>;
        const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
        return (
            ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
            !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
            dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
        );
    }

    /**
     * Gets the react node for product dimension as swatch.
     * @param  attributeValues - Attribute value property from product.
     * @returns The node representing markup for unit of measure component.
     */
    function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
        if (!attributeValues) {
            return null;
        }

        return (
            <div className='msc-product__dimensions'>
                {attributeValues.map((item: AttributeValue) => {
                    const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
                    if (!shouldDisplayDimension(dimensionTypeValue)) {
                        return null;
                    }

                    const siteContext = context as ICoreContext<IDimensionsApp>;
                    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
                    const dimensionType = dimensionTypeValue as DimensionTypes;
                    const swatches =
                        item.Swatches?.map<ISwatchItem>(swatchItem => {
                            return {
                                itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
                                value: swatchItem.SwatchValue ?? '',
                                dimensionType,
                                colorHexCode: swatchItem.SwatchColorHexCode,
                                imageUrl: swatchItem.SwatchImageUrl,
                                productImageUrls: swatchItem.ProductImageUrls,
                                isDefault: swatchItem.IsDefault,
                                swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
                                isDisabled:
                                    enableStockCheck &&
                                    dimensionAvailabilities?.find(
                                        dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
                                    )?.isDisabled
                            };
                        }) ?? [];
                    if (
                        dimensionType === dimensionToPreSelectInProductCard &&
                        ArrayExtensions.hasElements(swatches) &&
                        !swatches.some(swatch => swatch.isDefault)
                    ) {
                        swatches[0].isDefault = true;
                    }
                    return (
                        <ProductComponentSwatchComponent
                            context={context}
                            swatches={swatches}
                            onSelectDimension={updatePageAndImageUrl}
                            key={item.RecordId}
                        />
                    );
                })}
            </div>
        );
    }

    function renderSupersededProductText(extensionProps: CommerceProperty[] | undefined): JSX.Element | null {
        if (!extensionProps) {
            return null;
        }

        const supersededItemId = extensionProps ? extensionProps.find(prop => prop.Key === 'supersededItemId')?.Value?.StringValue : '';
        const alternateProductText = extensionProps
            ? extensionProps.find(prop => prop.Key === 'alternateProductText')?.Value?.StringValue
            : '';
        const supersededProductTextFallBack = context.app.config.supersededProductText;
        if (supersededItemId) {
            return (
                <>
                    {isAuthenticated ? (
                        <div className='superseded-item-text'>
                            {alternateProductText
                                ? alternateProductText?.replace('{}', supersededItemId)
                                : supersededProductTextFallBack?.replace('{}', supersededItemId)}
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            );
        } else {
            return null;
        }
    }

    function renderStockCheckProductText(extensionProps: CommerceProperty[] | undefined): JSX.Element | null {
        if (!extensionProps) {
            return null;
        }

        const stockAvailabilityText = extensionProps ? extensionProps.find(prop => prop.Key === 'stockCheckText')?.Value?.StringValue : '';
        const stockAvailabilityCode = extensionProps ? extensionProps.find(prop => prop.Key === 'stockCheckCode')?.Value?.StringValue : '';
        const stockAvail = stockAvailabilityCode === 'AVAIL';
        const stockLa = stockAvailabilityCode === 'LA';
        const stockOos = stockAvailabilityCode === 'OOS';
        if (stockAvailabilityText) {
            return (
                <>
                    {isAuthenticated ? (
                        <div
                            className={`stock-item-text ${
                                stockAvail ? 'available-item-text' : stockLa ? 'limited-item-text' : stockOos ? 'oos-item-text' : ''
                            }`}
                        >
                            {stockAvailabilityText}
                        </div>
                    ) : (
                        <></>
                    )}
                </>
            );
        } else {
            return null;
        }
    }

    const renderConditionalProductAttributes = (
        attributeValues?: AttributeValue[],
        fallbackText?: string,
        attributeKeyName?: string,
        attributeKeyNameText?: string,
        className?: string
    ): JSX.Element | null => {
        if (!attributeValues) {
            return null;
        }
        const productAttributeFallbackText = fallbackText && context.app.config[fallbackText];
        const isProductAttributeAvailable = attributeValues.find(attribute => attribute.KeyName === attributeKeyName)?.BooleanValue;
        const productAttributeText = attributeValues.find(attribute => attribute.KeyName === attributeKeyNameText)?.TextValue;

        if (isProductAttributeAvailable) {
            return <div className={className}>{productAttributeText ? productAttributeText : productAttributeFallbackText}</div>;
        }
        return null;
    };

    const renderDropshipProductText = (attributeValues?: AttributeValue[]): JSX.Element | null => {
        return renderConditionalProductAttributes(attributeValues, 'dropshipProductText', 'DropShip', 'DropShipText', 'dropship-item-text');
    };

    const renderSpecialOrderProductText = (attributeValues?: AttributeValue[]): JSX.Element | null => {
        return renderConditionalProductAttributes(
            attributeValues,
            'specialProductText',
            'SpecialOrder',
            'SpecialOrderText',
            'special-item-text'
        );
    };

    const renderRepairableProductText = (attributeValues?: AttributeValue[]): JSX.Element | null => {
        return renderConditionalProductAttributes(
            attributeValues,
            'repairableProductText',
            'Repairable',
            'RepairableText',
            'repairable-item-text'
        );
    };

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return (
            <div className='ms-quickView'>
                {React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions })}
            </div>
        );
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    let partNo = '';
    const productPricesView = (resources: ISearchResultContainerResources): JSX.Element => {
        let discount: number = 0;
        let DiscountAmount: number = 0;
        productPrices
            ?.filter(PLProduct => product?.ItemId === PLProduct?.ItemId)
            .map(data => {
                let discountedPrice: number = data?.BasePrice ?? 0;
                let originalPrice: number = data?.CustomerContextualPrice ?? 0;
                discount = Number((100 - (originalPrice / discountedPrice) * 100).toFixed(2));
                DiscountAmount = data?.DiscountAmount ?? 0;
            });
        return (
            <>
                {productPrices
                    ?.filter(PLProduct => product?.ItemId === PLProduct?.ItemId)
                    .map(data => {
                        data.ItemId ? (partNo = data.ItemId) : '';
                        const basePriceNum = data?.BasePrice?.toFixed(2);
                        const basePrice = basePriceNum && context.cultureFormatter.formatCurrency(basePriceNum);
                        const discountedPriceNum = data.CustomerContextualPrice?.toFixed(2);
                        const discountedPrice = discountedPriceNum && context.cultureFormatter.formatCurrency(discountedPriceNum);
                        const tradeAgreementPriceNum = data.TradeAgreementPrice?.toFixed(2);
                        const tradeAgreementPrice =
                            tradeAgreementPriceNum && context.cultureFormatter.formatCurrency(tradeAgreementPriceNum);
                        return (
                            <div key={data.ItemId} className='msc-product__price'>
                                {DiscountAmount > 0 ? (
                                    // discountedPrice
                                    //added toFixed(2) to round to 2 values after decimals
                                    //format(swatchItemAriaLabel, dimensionType)
                                    <>
                                        <div className='msc-product__price-original'>
                                            <span className='msc-product__price-original-text'>{resources.productCardPriceText}</span>
                                            <span className='msc-product__price-original-value'>{basePrice}</span>
                                            <span className='msc-product__price-original-discount'>
                                                {format(resources.productCardDiscountValue, discount)}
                                            </span>
                                        </div>
                                        <div className='msc-product__price-discounted'>
                                            <span className='msc-product__price-discounted-text'>{resources.productCardYourPriceText}</span>
                                            <span className='msc-product__price-discounted-value'>{discountedPrice}</span>
                                        </div>
                                    </>
                                ) : data.BasePrice !== data.AdjustedPrice &&
                                  data.BasePrice === data.TradeAgreementPrice &&
                                  DiscountAmount === 0 ? (
                                    //adjustedPrice
                                    //added toFixed(2) to round to 2 values after decimals
                                    <>
                                        <div className='msc-product__price-original'>
                                            <span className='msc-product__price-original-text'>{resources.productCardPriceText}</span>
                                            <span className='msc-product__price-original-value'>{basePrice}</span>
                                        </div>
                                        <div className='msc-product__price-discounted'>
                                            <span className='msc-product__price-discounted-text'>{resources.productCardYourPriceText}</span>
                                            <span className='msc-product__price-discounted-value'>{discountedPrice}</span>
                                        </div>
                                    </>
                                ) : data.BasePrice !== data.TradeAgreementPrice &&
                                  data.BasePrice !== data.AdjustedPrice &&
                                  data.TradeAgreementPrice !== data.AdjustedPrice ? (
                                    //tradeAgreementPrice && adjusted together scenario
                                    //added toFixed(2) to round to 2 values after decimals
                                    <>
                                        <div className='msc-product__price-original'>
                                            <span className='msc-product__price-original-text'>{resources.productCardPriceText}</span>
                                            <span className='msc-product__price-original-value'>{tradeAgreementPrice}</span>
                                        </div>
                                        <div className='msc-product__price-discounted'>
                                            <span className='msc-product__price-discounted-text'>{resources.productCardYourPriceText}</span>
                                            <span className='msc-product__price-discounted-value'>{discountedPrice}</span>
                                        </div>
                                    </>
                                ) : data.BasePrice !== data.TradeAgreementPrice ? (
                                    //tradeAgreementPrice
                                    //added toFixed(2) to round to 2 values after decimals
                                    <div className='msc-product__price-discounted'>
                                        <span className='msc-product__price-discounted-text'>{resources.productCardPriceText}</span>
                                        <span className='msc-product__price-discounted-value'>{discountedPrice}</span>
                                    </div>
                                ) : (
                                    //NormalPrice
                                    //added toFixed(2) to round to 2 values after decimals
                                    <div className='msc-product__price-discounted'>
                                        <span className='msc-product__price-discounted-text'>{resources.productCardPriceText}</span>
                                        <span className='msc-product__price-discounted-value'>{discountedPrice}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </>
        );
    };
    const productPriceItem = productPrices?.find(item => item.ItemId === product.ItemId);
    const defaultImageSettings: IImageSettings = {
        viewports: {
            xs: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            lg: { q: 'w=240&h=240&m=6', w: 0, h: 0 },
            xl: { q: 'w=240&h=240&m=6', w: 0, h: 0 }
        },
        lazyload: true
    };
    const maxQuantity = context.app.config.maxQuantityForCartLineItem;
    const [currentQuantity, setCurrentQuantity] = React.useState(1);
    const maxQuantityText = resources?.productCardMaxQuantityText?.replace('{max}', maxQuantity.toString());

    const onQuantityChange = (value: number) => {
        setCurrentQuantity(value);
        return true;
    };

    const hasDropshipText =
    product.AttributeValues?.find((attribute:AttributeValue) => attribute.KeyName === 'DropShip')?.BooleanValue ?? false;

    const hasSpecialOrderText =
    product.AttributeValues?.find((attribute:AttributeValue) => attribute.KeyName === 'SpecialOrder')?.BooleanValue ?? false;

    return (
        <>
            <div>
                {isEnabledProductDescription ? (
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={
                            isAuthenticated
                                ? renderLabel(
                                      product.Name,
                                      context.cultureFormatter.formatCurrency(product.Price),
                                      product.AverageRating,
                                      ratingAriaLabel,
                                      product.TotalRatings,
                                      ratingCountAriaLabel
                                  )
                                : ''
                        }
                        className='msc-product'
                        {...attribute}
                    >
                        <div className='msc-product__image__description'>
                            <div className='msc-product__image'>
                                {renderProductPlacementImage(
                                    imageSettings,
                                    context.request.gridSettings,
                                    productImageUrl,
                                    product.PrimaryImageUrl,
                                    product.Name,
                                    context
                                )}
                            </div>
                            <div className='msc-product__title_description'>
                                <h2 className='msc-product__title__text'>{product.Name}</h2>
                                {isAuthenticated && resources ? (
                                    productPricesView(resources)
                                ) : (
                                    <a href={user?.signInUrl} className='login-register-link'>
                                        {loginRegisterResource}
                                    </a>
                                )}
                                {renderDescription(product.Description)}
                            </div>
                        </div>
                    </a>
                ) : (
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={
                            isAuthenticated
                                ? renderLabel(
                                      product.Name,
                                      context.cultureFormatter.formatCurrency(product.Price),
                                      product.AverageRating,
                                      ratingAriaLabel
                                  )
                                : ''
                        }
                        className='msc-product'
                        {...attribute}
                    >
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__details'>
                            <h2 className='msc-product__title'>{product.Name}</h2>
                            {isAuthenticated && resources ? (
                                productPricesView(resources)
                            ) : (
                                <a href={user?.signInUrl} className='login-register-link'>
                                    {loginRegisterResource}
                                </a>
                            )}
                        </div>
                    </a>
                )}
            </div>
            {isAuthenticated ? (
                <div className='msc-product__attributes'>
                    <p className='msc-product__part-no'>
                        {resources?.productCardPartNoText}
                        {partNo}
                    </p>
                    {renderRepairableProductText(product.AttributeValues)}
                    {renderSupersededProductText(product.ExtensionProperties)}
                </div>
            ) : (
                <></>
            )}
            {renderProductDimensions(product.AttributeValues)}
            {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)}
            {renderProductAvailability(inventoryLabel)}
            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
            {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))}
            {isAuthenticated ? (
                <div>
                    {renderDropshipProductText(product.AttributeValues)}
                    {renderSpecialOrderProductText(product.AttributeValues)}
                    {/* {renderStockCheckProductText(product.ExtensionProperties)} */}
                    {!hasDropshipText && !hasSpecialOrderText && renderStockCheckProductText(product.ExtensionProperties)}
                    <IncrementalQuantity
                        id='ms-buybox__product-quantity-input'
                        max={maxQuantity}
                        currentCount={currentQuantity}
                        onChange={onQuantityChange}
                        inputQuantityAriaLabel={resources?.productCardInputQuantityAriaLabel}
                        decrementButtonAriaLabel={resources?.productCardDecrementButtonAriaLabel}
                        incrementButtonAriaLabel={resources?.productCardIncrementButtonAriaLabel}
                        minQuantityText={resources?.productCardMinQuantityText}
                        maxQuantityText={maxQuantityText}
                        telemetryContent={telemetryContent}
                    />
                    <AddToCartComponent
                        addToCartText={resources?.productCardAddToCartText as string}
                        outOfStockText={resources?.productCardOOSText as string}
                        navigationUrl={getUrlSync('cart', context.actionContext)}
                        quantity={currentQuantity}
                        data={{
                            product: {
                                ...product,
                                ProductTypeValue: ProductType.Standalone,
                                AdjustedPrice: productPriceItem?.AdjustedPrice!,
                                BasePrice: product.BasePrice!
                            },
                            price: productPriceItem
                        }}
                        context={context}
                        disabled={false}
                        id={id}
                        typeName={typeName}
                        onError={() => {}}
                        gridSettings={context.request.gridSettings}
                        imageSettings={defaultImageSettings}
                        telemetryContent={telemetryContent}
                        isOrderQuantityLimitsFeatureEnabled={true}
                        onAdd={() => {}}
                    />
                </div>
            ) : (
                <></>
            )}
        </>
    );
};

function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function renderDescription(description?: string): JSX.Element | null {
    return <p className='msc-product__text'>{description}</p>;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

function renderRating(
    coreContext: ICoreContext,
    moduleTypeName: string,
    moduleId: string,
    avgRating?: number,
    totalRatings?: number,
    ariaLabel?: string,
    ratingCountAriaLabel?: string
): JSX.Element | null {
    if (!avgRating) {
        return null;
    }

    const numberRatings = totalRatings?.toString() || undefined;
    const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
    const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

    return (
        <RatingComponent
            context={coreContext}
            id={moduleId}
            typeName={moduleTypeName}
            avgRating={avgRating}
            ratingCount={numberRatings}
            readOnly
            ariaLabel={ratingAriaLabelText}
            ratingCountAriaLabel={ratingCountAriaLabelText}
            data={{}}
        />
    );
}

function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }
    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };
    const imageProps = {
        gridSettings,
        imageSettings
    };
    imageProps.imageSettings.cropFocalRegion = true;
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

export const ProductComponent: React.FunctionComponent<IProductComponentProps> = msdyn365Commerce.createComponentOverride<
    IProductComponent
>('Product', { component: ProductCard, ...PriceComponentActions });

export default ProductComponent;
